import React, { useEffect, useState } from "react";
import { Stack, Typography, Alert, Grid, Divider } from "@mui/material";

import { Surface, SectionTitle, Kpi, DataTable, StatusChip } from "components/ui";
import { T, inr, tint } from "theme/tokens";
import * as api from "../api";

const when = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/**
 * What this partner is owed, and every movement behind it.
 *
 * Read-only. Adjusting a balance from a dashboard would create money with no
 * transaction behind it, which is exactly what `walletHistory` exists to make
 * impossible to do unnoticed — corrections belong in the payout flow, which
 * writes a history row.
 */
export default function WalletTab({ partnerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .getWallet(partnerId)
      .then((res) => alive && setData(res))
      .catch(
        (err) =>
          alive && setError(err?.response?.data?.message || "Could not load the wallet.")
      )
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [partnerId]);

  const history = data?.history || [];

  const credited = history
    .filter((row) => Number(row.amount) > 0)
    .reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const debited = history
    .filter((row) => Number(row.amount) < 0)
    .reduce((sum, row) => sum + Math.abs(Number(row.amount || 0)), 0);

  const columns = [
    {
      field: "date",
      headerName: "When",
      width: 190,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "0.8rem", color: T.muted }}>
          {when(row.date)}
        </Typography>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography sx={{ fontSize: "0.82rem", color: T.ink }}>
          {row.type || "—"}
        </Typography>
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 130,
      sortable: false,
      renderCell: ({ row }) => {
        const value = Number(row.amount || 0);
        return (
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              color: value < 0 ? T.error : T.success,
            }}
          >
            {value < 0 ? "−" : "+"}
            {inr(Math.abs(value))}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => <StatusChip status={row.status} label={row.status || "—"} />,
    },
    {
      field: "transactionId",
      headerName: "Reference",
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography
          sx={{ fontSize: "0.75rem", color: T.subtle, fontFamily: "monospace", wordBreak: "break-all" }}
        >
          {row.transactionId || "—"}
        </Typography>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      <Surface>
        <Grid container>
          {[
            {
              label: "Withdrawable",
              value: inr(data?.balance),
              sub: data?.hasBankDetails ? "bank on file" : "no bank on file",
              tone: data?.balance > 0 ? T.warning : T.muted,
            },
            { label: "Credited", value: inr(credited), sub: "in this history", tone: T.success },
            { label: "Debited", value: inr(debited), sub: "in this history", tone: T.error },
            { label: "Movements", value: history.length, sub: "most recent 100" },
          ].map((kpi, index) => (
            <Grid
              item
              xs={6}
              md={3}
              key={kpi.label}
              sx={{
                borderRight: { md: index < 3 ? `1px solid ${T.borderSoft}` : "none" },
                borderBottom: { xs: `1px solid ${T.borderSoft}`, md: "none" },
              }}
            >
              <Kpi {...kpi} loading={loading} />
            </Grid>
          ))}
        </Grid>
      </Surface>

      {!loading && data?.balance > 0 && !data?.hasBankDetails && (
        <Surface sx={{ p: 2, borderColor: tint(T.warning, 0.4), bgcolor: tint(T.warning, 0.06) }}>
          <Typography sx={{ fontSize: "0.85rem", color: T.ink }}>
            <b>{inr(data.balance)}</b> is owed but there is no bank account on
            file, so it cannot be paid out. Add one on the Bank tab.
          </Typography>
        </Surface>
      )}

      <Surface sx={{ p: { xs: 1.5, md: 2 } }}>
        <SectionTitle>Wallet movements</SectionTitle>
        <Divider sx={{ mb: 1.5 }} />
        <DataTable
          rows={history}
          columns={columns}
          loading={loading}
          emptyMessage="No wallet movements yet — the first one is written when a delivery is completed."
          getRowId={(row) =>
            row.transactionId || `${row.date}-${row.amount}-${row.type}`
          }
          height={420}
        />
        <Typography sx={{ fontSize: "0.76rem", color: T.subtle, mt: 1.2, px: 1 }}>
          Read-only. Balances move through the payout flow, which records a
          movement for every change — adjusting one from here would create money
          with no transaction behind it.
        </Typography>
      </Surface>
    </Stack>
  );
}
